/**
 * unlock.ts — declarative requirement evaluator shared by ores, buildings,
 * upgrades, workers, tech, quests and events. Centralising this avoids scattering
 * ad-hoc `if` checks across systems.
 */
import type { GameState } from '../core/GameState';
import type { Requirement } from '../data';

export type ExtendedRequirement = Requirement & {
  totalClicks?: number;
  resets?: number;
  ascensions?: number;
  nuggetsClicked?: number;
  abilitiesUsed?: number;
  maxCombo?: number;
};

/** Does the current state satisfy every clause of `req`? Empty/undefined = always true. */
export function meetsRequirement(state: GameState, req?: ExtendedRequirement): boolean {
  if (!req) return true;

  if (req.depth !== undefined && state.mining.maxDepth < req.depth) return false;
  if (req.money !== undefined && state.economy.money < req.money) return false;
  if (req.lifetimeEarnings !== undefined && state.stats.lifetimeEarnings < req.lifetimeEarnings)
    return false;
  if (req.totalOreMined !== undefined && state.stats.totalOreMined < req.totalOreMined) return false;
  if (req.totalClicks !== undefined && state.stats.totalClicks < req.totalClicks) return false;
  if (req.toolLevel !== undefined && state.mining.toolLevel < req.toolLevel) return false;
  if (req.resets !== undefined && state.prestige.resets < req.resets) return false;
  if (req.ascensions !== undefined && state.ascension.ascensions < req.ascensions) return false;
  if (req.nuggetsClicked !== undefined && state.stats.nuggetsClicked < req.nuggetsClicked) return false;
  if (req.abilitiesUsed !== undefined && state.stats.abilitiesUsed < req.abilitiesUsed) return false;
  if (req.maxCombo !== undefined && state.stats.maxCombo < req.maxCombo) return false;
  if (req.tech !== undefined && !state.research.researched[req.tech]) return false;

  if (req.building !== undefined) {
    const [id, count] = Array.isArray(req.building) ? req.building : [req.building, 1];
    if ((state.buildings[id] ?? 0) < count) return false;
  }

  return true;
}
